import { TmsStatePanel } from "@/components/tms/TmsStatePanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import * as React from "react";
import { useNavigate } from "react-router-dom";

type Exception = {
  id: string;
  load_id: string;
  type: string;
  severity: string;
  status: string;
  created_at: string;
  notes: string | null;
  reference_number: string;
  origin_city: string;
  origin_state: string;
  dest_city: string;
  dest_state: string;
  load_status: string;
};

const TYPE_LABEL: Record<string, string> = {
  unassigned_near_pickup: "Unassigned Near Pickup",
  tender_timeout: "Tender Timeout",
  late_pickup_risk: "Late Pickup Risk",
  late_delivery_risk: "Late Delivery Risk",
  no_recent_update: "No Recent Update",
};

const SEVERITY_BADGE: Record<string, string> = {
  critical: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  warning: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function ageLabel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function ExceptionsPage() {
  const navigate = useNavigate();
  const [exceptions, setExceptions] = React.useState<Exception[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState("");
  const [actioning, setActioning] = React.useState<string | null>(null);

  const fetchExceptions = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ status: "open" });
      if (severityFilter) params.set("severity", severityFilter);
      if (typeFilter) params.set("type", typeFilter);
      const res = await fetch(`/api/tms/exceptions?${params.toString()}`);
      const json = (await res.json()) as { status: string; data?: Exception[]; error?: string };
      if (json.status === "success" && json.data) {
        setExceptions(json.data);
      } else {
        setExceptions([]);
        setError(json.error ?? "Failed to load exceptions.");
      }
    } catch (e) {
      setExceptions([]);
      setError(e instanceof Error ? e.message : "Network error.");
    } finally {
      setLoading(false);
    }
  }, [severityFilter, typeFilter]);

  React.useEffect(() => {
    void fetchExceptions();
  }, [fetchExceptions]);

  async function handleAction(id: string, action: "acknowledged" | "resolved") {
    setActioning(id);
    const res = await fetch(`/api/tms/exceptions/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: action }),
    });
    const json = (await res.json()) as { status: string };
    if (json.status === "success") {
      setExceptions((prev) => prev.filter((e) => e.id !== id));
    }
    setActioning(null);
  }

  const criticalCount = exceptions.filter((e) => e.severity === "critical").length;
  const hasFilters = Boolean(severityFilter || typeFilter);

  function clearFilters() {
    setSeverityFilter("");
    setTypeFilter("");
  }

  const summaryText = loading
    ? "Loading open exceptions..."
    : error
      ? "We couldn't load the exception queue."
      : hasFilters
        ? `${exceptions.length} matching exception${exceptions.length !== 1 ? "s" : ""}${criticalCount > 0 ? `, ${criticalCount} critical` : ""}`
        : `${exceptions.length} open exception${exceptions.length !== 1 ? "s" : ""}${criticalCount > 0 ? `, ${criticalCount} critical` : ""}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Exception Queue</h1>
          <p className="text-sm text-muted-foreground">{summaryText}</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap gap-3 items-center">
            <select
              className="h-8 rounded-md border border-input bg-background px-3 text-sm"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
            </select>
            <select
              className="h-8 rounded-md border border-input bg-background px-3 text-sm"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All Types</option>
              {Object.entries(TYPE_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : error ? (
            <TmsStatePanel
              tone="error"
              title="We couldn't load the exception queue."
              description={error}
              actionLabel="Retry"
              onAction={() => void fetchExceptions()}
            />
          ) : exceptions.length === 0 ? (
            <TmsStatePanel
              tone={hasFilters ? "filtered" : "empty"}
              title={hasFilters ? "No exceptions match these filters." : "No open exceptions."}
              description={
                hasFilters
                  ? "Try a different severity or type filter, or clear the filters to see the full queue."
                  : "Your queue is clear. New issues will appear here when loads need attention."
              }
              actionLabel={hasFilters ? "Clear filters" : undefined}
              onAction={hasFilters ? clearFilters : undefined}
            />
          ) : (
            <div className="space-y-2">
              {exceptions.map((ex) => (
                <div
                  key={ex.id}
                  className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
                    ex.severity === "critical" ? "border-red-200 bg-red-50/50 dark:border-red-900/30 dark:bg-red-900/5" : "border-yellow-200 bg-yellow-50/50 dark:border-yellow-900/30 dark:bg-yellow-900/5"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        className="font-medium text-sm hover:underline"
                        onClick={() => navigate(`/dispatch/loads/${ex.load_id}`)}
                      >
                        {ex.reference_number}
                      </button>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_BADGE[ex.severity] ?? ""}`}>
                        {ex.severity}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {ageLabel(ex.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{TYPE_LABEL[ex.type] ?? ex.type}</p>
                    <p className="text-xs text-muted-foreground">{ex.origin_city}, {ex.origin_state} → {ex.dest_city}, {ex.dest_state}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={actioning === ex.id}
                      onClick={() => void handleAction(ex.id, "acknowledged")}
                    >
                      Acknowledge
                    </Button>
                    <Button
                      size="sm"
                      disabled={actioning === ex.id}
                      onClick={() => void handleAction(ex.id, "resolved")}
                    >
                      Resolve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
